/**
 * 软件主题 ↔ 品牌（Brand）桥接层。
 *
 * 把"读取当前 brand 的 CSS token、daylight 浅色预设由 brand 重算、
 * brand 接管的 token 还原"这类与 brand 系统耦合的逻辑从主文件抽出，
 * 让 software_theme.ts 专注于状态/调度。
 *
 * 这里所有函数都是按调用即取最新 brand 属性，无模块状态缓存。
 */

import { getBrandThemeTokens, normalizeBrandValue } from '@/brand/theme'
import { mixHexColor as mixColor, normalizeHexColor, withAlpha as withOpacity } from '@/utils/color'
import type { ThemeSeed } from '@/service/software_theme_tokens'

export type SoftwareThemePreset = 'daylight' | 'ocean' | 'violet' | 'sakura' | 'night'

export type SoftwareThemeOption = {
  value: SoftwareThemePreset
  labelKey: string
  swatches: [string, string, string]
}

export const presetKeys = ['daylight', 'ocean', 'violet', 'sakura', 'night'] as const
export const visiblePresetKeys = ['daylight', 'ocean', 'violet', 'sakura', 'night'] as const

/**
 * 默认预设的种子色。
 *
 * daylight（极简灰）有意与 brand 解耦：领导反馈"暖阳白"由橙系 brand-1 派生时整体偏粉，
 * 改为中性冷灰白底 + 沉稳深石板灰 accent，给重视专注与正式感的用户。
 * 如需再次让 daylight 跟随 brand 派生，恢复 resolveDaylightSeed 的 brand 混色逻辑即可。
 */
export const defaultPresetSeeds: Record<SoftwareThemePreset, ThemeSeed> = {
  daylight: {
    stage: '#eef0f3',
    accent: '#4f6079',
    depth: 18,
    tone: 'light',
  },
  ocean: {
    stage: '#64b5f6',
    accent: '#1e60d0',
    depth: 62,
    tone: 'dark',
  },
  violet: {
    stage: '#c49cff',
    accent: '#7c3aed',
    depth: 56,
    tone: 'dark',
  },
  sakura: {
    stage: '#ffafc5',
    accent: '#ef4080',
    depth: 40,
    tone: 'dark',
  },
  night: {
    stage: '#182436',
    accent: '#10cfff',
    depth: 90,
    tone: 'dark',
  },
}

/**
 * 预设卡片额外的“软色”（swatches[0]，卡片内亮部小键）。
 * 与 seed.stage/accent 存于同一表以保证“一个预设 = 一组色彩”的单源事实。
 * night 预设的软色是从 accent 派生的冷色，其他为从 stage 派生。
 */
const defaultPresetSoftSwatches: Record<SoftwareThemePreset, string> = {
  daylight: '#ffffff',
  ocean: '#dff0ff',
  violet: '#f0e6ff',
  sakura: '#fff0f4',
  night: '#dff0ff',
}

const defaultSoftwareThemeOptions: Record<SoftwareThemePreset, Pick<SoftwareThemeOption, 'value' | 'labelKey'>> = {
  daylight: { value: 'daylight', labelKey: 'software_theme_daylight' },
  ocean: { value: 'ocean', labelKey: 'software_theme_ocean' },
  violet: { value: 'violet', labelKey: 'software_theme_violet' },
  sakura: { value: 'sakura', labelKey: 'software_theme_sakura' },
  night: { value: 'night', labelKey: 'software_theme_night' },
}

/**
 * brand 系统接管、不允许 software_theme 覆盖的 token 集合。
 *
 * 当用户切回"system"模式或更换 brand 时，需要把这些 key 从 software_theme 的写入中
 * 让位给 brand 的真实值，避免出现品牌色被旧主题残留遮盖的情况。
 *
 * 维护规则参考：.github/instructions/design-system.instructions.md
 */
export const brandManagedThemeTokenKeys = [
  '--color-brand-1',
  '--color-brand-1-rgb',
  '--color-brand-2',
  '--color-brand-2-rgb',
  '--brand-color',
  '--brand-color-pure',
  '--brand-color-rgb',
  '--brand-color-pure-rgb',
  '--brand-accent-color',
  '--brand-accent-rgb',
  '--brand-fn-color',
  '--bg-main',
  '--sidebar-item-active',
] as const

/**
 * 读取当前生效 brand 的 token 表；SSR/早期初始化时退化到 IROK。
 */
export const getActiveBrandTokens = () => {
  if (typeof document === 'undefined') {
    return getBrandThemeTokens('IROK')
  }

  const root = document.documentElement
  const body = document.body
  const activeBrand = normalizeBrandValue(root.getAttribute('brand') ?? body.getAttribute('brand')) ?? 'IROK'

  return getBrandThemeTokens(activeBrand)
}

/**
 * daylight 预设：浅色路径。
 *
 * 历史版本会从当前 brand 派生（brand-1 混白），但因为 brand-1 多为橙系色，整体观感偏粉，
 * 现已固定为中性灰白色板（见 defaultPresetSeeds.daylight），不再读 brand。
 * 函数保留是为了 swatches 解析链路统一，并给将来想再次跟随 brand 时留好接入点。
 */
export const resolveDaylightSeed = (): ThemeSeed => {
  return { ...defaultPresetSeeds.daylight }
}

export const resolvePresetSeeds = (): Record<SoftwareThemePreset, ThemeSeed> => {
  return {
    ...defaultPresetSeeds,
    daylight: resolveDaylightSeed(),
  }
}

export const resolveSoftwareThemeOptions = (): SoftwareThemeOption[] => {
  // 卡片 swatches 统一从 seed + softSwatch 派生，避免“卡片预览 / 点击后主题”双源不一致。
  return visiblePresetKeys.map((preset) => {
    const meta = defaultSoftwareThemeOptions[preset]
    const seed = preset === 'daylight' ? resolveDaylightSeed() : defaultPresetSeeds[preset]
    return {
      value: meta.value,
      labelKey: meta.labelKey,
      swatches: [defaultPresetSoftSwatches[preset], seed.stage, seed.accent],
    }
  })
}

/**
 * "跟随系统"卡片的预览三色：基于当前 brand 计算，跟 createThemeTokens 不挂钩。
 */
export const resolveSystemPreviewPalette = () => {
  const brandTokens = getActiveBrandTokens()
  const stage = normalizeHexColor(brandTokens['--color-brand-2'], '#ff8f60')
  const accent = normalizeHexColor(brandTokens['--color-brand-1'], '#FF8F60')
  const lightSurface = mixColor(stage, '#ffffff', 0.72)

  return {
    card: `linear-gradient(180deg, ${withOpacity(mixColor(stage, '#ffffff', 0.88), 0.96)} 0%, ${withOpacity(mixColor(accent, '#ffffff', 0.8), 0.88)} 100%)`,
    soft: lightSurface,
    brand: accent,
    deep: stage,
  }
}
